import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Search,
  Merge,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  Users,
  FileText,
  FolderOpen,
} from "lucide-react";
import { toast } from "sonner";
import { ConfirmDialog } from "../ui/confirm-dialog";

export function DeduplicationTab() {
  const [scanning, setScanning] = useState(false);
  const [groups, setGroups] = useState([]);
  const [scanned, setScanned] = useState(false);
  const [merging, setMerging] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [mergeAllConfirm, setMergeAllConfirm] = useState(false);
  const [selections, setSelections] = useState({});
  const [mergeResults, setMergeResults] = useState([]);

  const handleScan = async () => {
    setScanning(true);
    setMergeResults([]);
    try {
      const res = await axios.get("/api/clients/duplicates/scan");
      setGroups(res.data.duplicate_groups || []);
      setScanned(true);
      // Auto-select client with most policies as default
      const sel = {};
      for (let i = 0; i < (res.data.duplicate_groups || []).length; i++) {
        const g = res.data.duplicate_groups[i];
        const best = [...g.clients].sort(
          (a, b) => (b.policy_count + b.document_count) - (a.policy_count + a.document_count)
        )[0];
        sel[i] = best.client_id;
      }
      setSelections(sel);
      if (!res.data.duplicate_groups?.length) {
        toast.success("Nessun duplicato trovato!");
      } else {
        toast.info(`Trovati ${res.data.total_groups} gruppi di duplicati`);
      }
    } catch {
      toast.error("Errore nella scansione");
    } finally {
      setScanning(false);
    }
  };

  const handleMerge = async (groupIndex) => {
    const group = groups[groupIndex];
    const keepId = selections[groupIndex];
    if (!keepId) {
      toast.error("Seleziona il cliente da mantenere");
      return;
    }
    const mergeIds = group.clients
      .map((c) => c.client_id)
      .filter((id) => id !== keepId);

    setConfirmDialog({ groupIndex, keepId, mergeIds, group });
  };

  const confirmMerge = async () => {
    const { groupIndex, keepId, mergeIds } = confirmDialog;
    setConfirmDialog(null);
    setMerging(true);
    try {
      const res = await axios.post("/api/clients/duplicates/merge", {
        keep_client_id: keepId,
        merge_client_ids: mergeIds,
      });
      setMergeResults((prev) => [...prev, { groupIndex, result: res.data }]);
      // Remove merged group
      setGroups((prev) => prev.filter((_, i) => i !== groupIndex));
      toast.success(
        `Unione completata: ${res.data.policies_moved} polizze, ${res.data.documents_moved} documenti spostati`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'unione");
    } finally {
      setMerging(false);
    }
  };

  const handleMergeAll = () => {
    if (!groups.length) return;
    setMergeAllConfirm(true);
  };

  const handleMergeAllConfirmed = async () => {
    setMergeAllConfirm(false);
    setMerging(true);
    let successCount = 0;
    const remaining = [...groups];

    for (let i = remaining.length - 1; i >= 0; i--) {
      const group = remaining[i];
      const keepId = selections[i];
      if (!keepId) continue;
      const mergeIds = group.clients
        .map((c) => c.client_id)
        .filter((id) => id !== keepId);
      try {
        const res = await axios.post("/api/clients/duplicates/merge", {
          keep_client_id: keepId,
          merge_client_ids: mergeIds,
        });
        setMergeResults((prev) => [...prev, { groupIndex: i, result: res.data }]);
        successCount++;
      } catch {
        toast.error(`Errore nell'unione del gruppo ${i + 1}`);
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} gruppi uniti con successo`);
      setGroups([]);
    }
    setMerging(false);
  };

  const reasonLabel = (reason) => {
    if (reason === "fiscal_code") return "Codice Fiscale";
    return "Nome";
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2" data-testid="dedup-title">
            <Users className="h-5 w-5" />
            Gestione Duplicati Clienti
          </CardTitle>
          <CardDescription>
            Scansiona il database per trovare clienti duplicati e uniscili. Polizze, documenti, file
            locali e MEGA verranno spostati automaticamente.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3">
            <Button
              onClick={handleScan}
              disabled={scanning}
              data-testid="scan-duplicates-btn"
            >
              {scanning ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Search className="h-4 w-4 mr-2" />
              )}
              {scanning ? "Scansione..." : "Scansiona Duplicati"}
            </Button>
            {groups.length > 0 && (
              <Button
                variant="destructive"
                onClick={handleMergeAll}
                disabled={merging}
                data-testid="merge-all-btn"
              >
                {merging ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Merge className="h-4 w-4 mr-2" />
                )}
                Unisci Tutti ({groups.length})
              </Button>
            )}
          </div>

          {scanned && groups.length === 0 && (
            <div className="flex items-center gap-2 p-4 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="text-green-700 dark:text-green-300">
                Nessun duplicato trovato. Il database è pulito.
              </span>
            </div>
          )}

          {groups.map((group, gi) => (
            <Card key={gi} className="border-amber-200 dark:border-amber-800">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    <span className="font-medium">
                      Gruppo {gi + 1}: {group.clients.length} duplicati
                    </span>
                    <Badge variant="outline">{reasonLabel(group.reason)}: {group.match_value}</Badge>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleMerge(gi)}
                    disabled={merging}
                    data-testid={`merge-group-${gi}-btn`}
                  >
                    <Merge className="h-3 w-3 mr-1" />
                    Unisci
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={selections[gi] || ""}
                  onValueChange={(val) =>
                    setSelections((prev) => ({ ...prev, [gi]: val }))
                  }
                >
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">Mantieni</TableHead>
                        <TableHead>Nome</TableHead>
                        <TableHead>Codice Fiscale</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Polizze</TableHead>
                        <TableHead>Documenti</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {group.clients.map((client) => (
                        <TableRow
                          key={client.client_id}
                          className={
                            selections[gi] === client.client_id
                              ? "bg-blue-50 dark:bg-blue-950"
                              : ""
                          }
                        >
                          <TableCell>
                            <RadioGroupItem
                              value={client.client_id}
                              id={`radio-${gi}-${client.client_id}`}
                              data-testid={`select-client-${client.client_id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Label
                              htmlFor={`radio-${gi}-${client.client_id}`}
                              className="cursor-pointer font-medium"
                            >
                              {client.name}
                            </Label>
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {client.fiscal_code || "-"}
                          </TableCell>
                          <TableCell className="text-sm">
                            {client.email || "-"}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <FileText className="h-3 w-3" />
                              {client.policy_count}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <FolderOpen className="h-3 w-3" />
                              {client.document_count}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </RadioGroup>
                <p className="text-xs text-muted-foreground mt-2">
                  Seleziona il cliente da mantenere. Tutti gli altri verranno uniti in quello selezionato.
                </p>
              </CardContent>
            </Card>
          ))}

          {mergeResults.length > 0 && (
            <Card className="border-green-200 dark:border-green-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Risultati Unione
                </CardTitle>
              </CardHeader>
              <CardContent>
                {mergeResults.map((mr, i) => (
                  <div key={i} className="text-sm py-1 border-b last:border-0">
                    <span className="font-medium">{mr.result.kept_client}</span>
                    <span className="text-muted-foreground">
                      {" "}&larr; {mr.result.merged_clients?.join(", ")}
                    </span>
                    <span className="ml-2 text-xs">
                      ({mr.result.policies_moved} polizze, {mr.result.documents_moved} doc DB
                      {mr.result.local_files_moved > 0 && `, ${mr.result.local_files_moved} file locali`}
                      {mr.result.drive_files_moved > 0 && `, ${mr.result.drive_files_moved} file MEGA`})
                    </span>
                    {mr.result.errors?.length > 0 && (
                      <div className="text-amber-600 text-xs mt-1">
                        {mr.result.errors.map((e, ei) => (
                          <div key={ei}>{e}</div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      {/* Conferma UNISCI TUTTI (operazione di massa irreversibile) */}
      <ConfirmDialog
        open={mergeAllConfirm}
        onOpenChange={setMergeAllConfirm}
        title="Unire TUTTI i gruppi di duplicati?"
        description={
          `Verranno uniti ${groups.length} gruppi in una sola volta: per ogni gruppo le polizze, i documenti e le ` +
          `notifiche passano al cliente scelto e gli altri clienti vengono ELIMINATI. L'operazione NON è annullabile.`
        }
        details={<span>Consiglio: unisci prima un gruppo alla volta per controllare il risultato.</span>}
        confirmText="Unisci tutti"
        requireText="UNISCI"
        variant="danger"
        loading={merging}
        onConfirm={handleMergeAllConfirmed}
      />

      {/* Confirm Merge Dialog */}
      <Dialog open={!!confirmDialog} onOpenChange={() => setConfirmDialog(null)}>
        <DialogContent aria-describedby="merge-confirm-desc">
          <DialogHeader>
            <DialogTitle>Conferma Unione Clienti</DialogTitle>
            <DialogDescription id="merge-confirm-desc">
              Questa operazione non è reversibile.
            </DialogDescription>
          </DialogHeader>
          {confirmDialog && (
            <div className="space-y-3 py-2">
              <p className="text-sm">
                <span className="font-medium">Mantenere:</span>{" "}
                {confirmDialog.group.clients.find(
                  (c) => c.client_id === confirmDialog.keepId
                )?.name}
              </p>
              <p className="text-sm">
                <span className="font-medium">Eliminare e unire:</span>{" "}
                {confirmDialog.group.clients
                  .filter((c) => confirmDialog.mergeIds.includes(c.client_id))
                  .map((c) => c.name)
                  .join(", ")}
              </p>
              <div className="p-3 bg-amber-50 dark:bg-amber-950 rounded-lg text-sm">
                <p>Verranno spostati:</p>
                <ul className="list-disc list-inside text-xs mt-1 space-y-0.5">
                  <li>Tutte le polizze nel cliente mantenuto</li>
                  <li>Tutti i documenti (DB, file locali, MEGA)</li>
                  <li>Notifiche e dati anagrafici mancanti</li>
                </ul>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog(null)}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={confirmMerge} disabled={merging} data-testid="confirm-merge-btn">
              {merging ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Merge className="h-4 w-4 mr-2" />
              )}
              Conferma Unione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
